import { useEffect, useRef, useState } from 'react'

type UseDelayedLoadingOptions = {
  /**
   * Milliseconds to wait before the loading state becomes visible.
   * Matches `--motion-delay-loading-reveal`.
   * @default 300
   */
  delay?: number
  /**
   * Milliseconds the loading state stays visible once shown.
   * @default 400
   */
  minDuration?: number
}

/**
 * Hook that turns a raw loading flag into a flicker-free visible state.
 *
 * The visible state stays false until `loading` has been true for `delay`
 * milliseconds, so fast responses never flash an indicator. Once visible, it
 * stays true for at least `minDuration` milliseconds, so the indicator never
 * flashes when the response arrives just after the delay.
 *
 * @see the Foundations → Motion guide, "Loading and progress"
 *
 * @example
 * const showSpinner = useDelayedLoading(isFetching)
 *
 * return showSpinner ? <SimpleSpinner /> : <PostList posts={posts} />
 */
export const useDelayedLoading = (
  loading: boolean,
  { delay = 300, minDuration = 400 }: UseDelayedLoadingOptions = {},
): boolean => {
  const [visible, setVisible] = useState(false)
  const shownAtRef = useRef(0)

  useEffect(() => {
    if (loading && !visible) {
      const timeout = setTimeout(() => {
        shownAtRef.current = Date.now()
        setVisible(true)
      }, delay)

      return () => clearTimeout(timeout)
    }

    if (!loading && visible) {
      const shownFor = Date.now() - shownAtRef.current
      const remaining = Math.max(0, minDuration - shownFor)
      const timeout = setTimeout(() => setVisible(false), remaining)

      return () => clearTimeout(timeout)
    }

    return undefined
  }, [loading, visible, delay, minDuration])

  return visible
}
